import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Loader2, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { ComponentType } from "react";

export interface BottomNavItem {
  path: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  exact?: boolean;
}

interface BottomNavProps {
  items: BottomNavItem[];
  maxVisible?: number;
}

export function BottomNav({ items, maxVisible = 4 }: BottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    navigate("/login");
  };

  // Close overflow menu when navigating to a new page
  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  const hasOverflow = items.length > maxVisible;
  const primaryItems = hasOverflow ? items.slice(0, maxVisible) : items;
  const overflowItems = hasOverflow ? items.slice(maxVisible) : [];

  const isItemActive = (item: BottomNavItem) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const isOverflowActive = overflowItems.some(isItemActive);

  const tabClass = (active: boolean) =>
    `flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
      active ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <>
      {/* Overflow backdrop */}
      {showMore && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setShowMore(false)}
        />
      )}

      {/* Overflow menu */}
      {showMore && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 right-0 z-50 md:hidden px-4 pb-2">
          <div className="bg-card border border-border rounded-lg shadow-xl overflow-hidden">
            {overflowItems.map((item) => {
              const active = isItemActive(item);
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    setShowMore(false);
                    navigate(item.path);
                  }}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors ${
                    active
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
            <div className="border-t border-border">
              <button
                onClick={() => {
                  setShowMore(false);
                  handleLogout();
                }}
                disabled={isLoggingOut}
                className="w-full px-4 py-3 text-left flex items-center gap-3 transition-colors text-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-60"
              >
                {isLoggingOut ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">Wyloguj</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border md:hidden pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16">
          {primaryItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => tabClass(isActive)}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          ))}
          {hasOverflow ? (
            <button
              onClick={() => setShowMore(!showMore)}
              className={tabClass(showMore || isOverflowActive)}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium">Więcej</span>
            </button>
          ) : (
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex flex-col items-center justify-center gap-1 flex-1 h-full text-muted-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <LogOut className="w-5 h-5" />
              )}
              <span className="text-[10px] font-medium">Wyloguj</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
